import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import DButton from "discourse/ui-kit/d-button";
import DModal from "discourse/ui-kit/d-modal";
import { i18n } from "discourse-i18n";

// Pre-rename key, kept so saved preferences survive.
const MESH_PRIVACY_ACK_KEY = "voice_mesh_privacy_ack";

// Guards against a repeat join request (double click, sidebar plus room
// page) replacing the modal out from under the first one.
let warningOpen = false;

function acknowledged() {
  try {
    return localStorage.getItem(MESH_PRIVACY_ACK_KEY) === "true";
  } catch {
    return false;
  }
}

function rememberAcknowledgement() {
  try {
    localStorage.setItem(MESH_PRIVACY_ACK_KEY, "true");
  } catch {
    // ignore storage errors
  }
}

// Mesh joins connect participants' browsers directly, so everyone in the
// room can learn everyone else's IP address. Resolves to whether the join
// may proceed, gating mesh rooms on an explicit acknowledgement; "don't
// show this again" persists per device.
export async function confirmMeshPrivacy(
  room,
  { currentUser, modal, siteSettings }
) {
  if (
    !siteSettings.live_mesh_privacy_warning_enabled ||
    !currentUser ||
    room.expected_transport !== "mesh"
  ) {
    return true;
  }

  if (acknowledged()) {
    return true;
  }

  if (warningOpen) {
    return false;
  }

  warningOpen = true;
  try {
    const result = await modal.show(LiveMeshPrivacyWarningModal);
    if (!result?.join) {
      return false;
    }
    if (result.dontShowAgain) {
      rememberAcknowledgement();
    }
    return true;
  } finally {
    warningOpen = false;
  }
}

// Resolves through `closeModal` with `{ join: true, dontShowAgain }` when the
// user accepts; cancel/escape/backdrop resolve with nothing.
export default class LiveMeshPrivacyWarningModal extends Component {
  @tracked dontShowAgain = false;

  @action
  updateDontShowAgain(event) {
    this.dontShowAgain = event.target.checked;
  }

  @action
  join() {
    this.args.closeModal({ join: true, dontShowAgain: this.dontShowAgain });
  }

  <template>
    <DModal
      class="live-mesh-privacy-warning-modal"
      @closeModal={{@closeModal}}
      @title={{i18n "live.mesh_privacy_warning.title"}}
    >
      <:body>
        <p class="live-mesh-privacy-warning-modal__body">
          {{i18n "live.mesh_privacy_warning.body"}}
        </p>
        <label class="live-mesh-privacy-warning-modal__dont-show-again">
          <input
            checked={{this.dontShowAgain}}
            type="checkbox"
            {{on "change" this.updateDontShowAgain}}
          />
          {{i18n "live.mesh_privacy_warning.dont_show_again"}}
        </label>
      </:body>
      <:footer>
        <DButton
          class="btn-primary live-mesh-privacy-warning-modal__join"
          @action={{this.join}}
          @icon="phone"
          @label="live.mesh_privacy_warning.join"
        />
        <DButton
          class="btn-flat live-mesh-privacy-warning-modal__cancel"
          @action={{@closeModal}}
          @label="live.mesh_privacy_warning.cancel"
        />
      </:footer>
    </DModal>
  </template>
}
